import { useEffect, useRef } from "react";
import { CreateAlertFormValues } from "../types";

interface UseFormValuesSyncParams {
	values: CreateAlertFormValues;
	onSync: (values: CreateAlertFormValues) => void;
	isOpen: boolean;
	debounceMs?: number;
}

/**
 * Text fields that change on every keystroke. These are excluded from
 * the shallow-comparison that decides whether to trigger a debounced sync,
 * because syncing them to the MobX store on every keystroke causes
 * enableReinitialize to reset the Formik form (new object reference)
 * creating a catastrophic re-render loop:
 *   keystroke → values change → sync to MobX → new initialValues ref
 *   → enableReinitialize resets form → new values → repeat
 *
 * These fields are still included in the payload sent to onSync when a
 * *non-text* field changes (e.g. templateType, geofence), so they are
 * never lost.
 */
const TEXT_FIELDS_TO_SKIP: (keyof CreateAlertFormValues)[] = [
	'name',
	'customTitle',
	'customDescription',
];

/** Additional text fields from extended form types (e.g. CreateEventFormValues) */
const EXTENDED_TEXT_FIELDS_TO_SKIP = ['resume'];

export function useFormValuesSync({
	values,
	onSync,
	isOpen,
	debounceMs = 300,
}: UseFormValuesSyncParams) {
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const previousValuesRef = useRef<CreateAlertFormValues | null>(null);

	useEffect(() => {
		if (!isOpen) return;

		const criticalFields = ['geofence', 'locationId', 'templateType', 'area'];
		const previousValues = previousValuesRef.current;

		const hasCriticalChange = previousValues && criticalFields.some(
			(field) => {
				const key = field as keyof CreateAlertFormValues;
				return JSON.stringify(previousValues[key]) !== JSON.stringify(values[key]);
			}
		);

		if (hasCriticalChange) {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
			onSync(values);
			previousValuesRef.current = values;
			return;
		}

		// Check if only text fields changed — if so, skip the sync entirely
		// to avoid the re-render loop caused by MobX → enableReinitialize
		const allSkipFields = [...TEXT_FIELDS_TO_SKIP, ...EXTENDED_TEXT_FIELDS_TO_SKIP];
		if (previousValues) {
			const hasNonTextChange = Object.keys(values).some((field) => {
				if (allSkipFields.includes(field as any)) return false;
				const key = field as keyof CreateAlertFormValues;
				return JSON.stringify(previousValues[key]) !== JSON.stringify(values[key]);
			});

			if (!hasNonTextChange) {
				// Only text fields changed — do NOT sync to store
				return;
			}
		}

		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		timeoutRef.current = setTimeout(() => {
			onSync(values);
			previousValuesRef.current = values;
		}, debounceMs);

		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, [values, onSync, isOpen, debounceMs]);
}
